import { ActionType, UpdatePreferencesAction } from '@/actions/actiontypes';
import AppModal from '@/components/common/AppModal';
import { statePreferences, stateSplash } from '@/selectors/selectors';
import { API_VERSION_STORAGE_KEY, ApiVersion, PreferencesState } from '@/state';
import { API_ENVIRONMENT_STORAGE_KEY, ApiEnvironment } from '@/state';
import { SplashState } from '@/state/SplashState';
import { useDispatch, useSelector } from '@/state/store';
import { useState } from 'react';
import SimpleSelect from '../objects/SimpleSelect';
import SimpleToggle from '../objects/SimpleToggle';
import { getApiEnvironment, runtimeEnvironment } from '@/config';
import { getApiVersion } from '@/utils/Utils';
import { apiUpdateAction, setApiEnvironment } from '@/actions/metaActions';
import { DatakatalogMode } from '@/components/avgrensning/datakatalog/constants';
import { API_ENVIRONMENT_LABELS } from '@/config';

interface PreferencesDialogProps {
    handleWindowChange: (window: SplashState) => void;
}

const DATAKATALOG_MODE_LABELS: Record<DatakatalogMode, string> = {
    [DatakatalogMode.SMALL]: 'Liten visning',
    [DatakatalogMode.BIG]: 'Stor visning',
    [DatakatalogMode.FLOATING_WINDOW]: 'Eget vindu',
};

export const PreferencesDialog = ({ handleWindowChange }: PreferencesDialogProps) => {
    const preferences = useSelector(statePreferences);
    const modalState = useSelector(stateSplash);
    const dispatch = useDispatch();

    const [selectedPreferences, setSelectedPreferences] =
        useState<PreferencesState>(preferences);
    const [showWarning, setShowWarning] = useState(false);
    const [selectedApiVersion, setSelectedApiVersion] = useState(getApiVersion());
    const [selectedApiEnvironment, setSelectedApiEnvironment] =
        useState(getApiEnvironment());

    const handleToggle = (name: keyof PreferencesState) => {
        setSelectedPreferences(prev => prev.override({ [name]: !prev[name] }));
    };

    const handleEnvironmentSelect = (env: ApiEnvironment) => {
        setSelectedApiEnvironment(env);

        // If API environment is an 'elveg' environment, disable use of egengeometri; otherwise enable it
        const elvegEnvs: ApiEnvironment[] = ['elvegutv', 'elvegtest', 'elvegprod'];
        const useEgengeometri = !elvegEnvs.includes(env);
        setSelectedPreferences(prev =>
            prev.override({ useEgengeometriWhenAvailable: useEgengeometri })
        );
    };

    const handleDatakatalogModeSelect = (mode: DatakatalogMode) => {
        setSelectedPreferences(
            selectedPreferences.override({
                datakatalogDefaultMode: mode,
            })
        );
    };

    const hasUnsavedChanges = () => {
        return (
            JSON.stringify(preferences) !== JSON.stringify(selectedPreferences) ||
            getApiVersion() !== selectedApiVersion
        );
    };

    const handleClose = () => {
        if (hasUnsavedChanges() && !showWarning) {
            setShowWarning(true);
        } else {
            setShowWarning(false);
            handleWindowChange(SplashState.NONE);
        }
    };

    const handleSubmit = () => {
        const action: UpdatePreferencesAction = {
            type: ActionType.PREFERENCES_UPDATE,
            ...selectedPreferences,
        };
        dispatch(action);
        handleWindowChange(SplashState.NONE);

        sessionStorage.setItem(API_VERSION_STORAGE_KEY, selectedApiVersion);
        dispatch(apiUpdateAction());

        if (
            selectedApiEnvironment !== sessionStorage.getItem(API_ENVIRONMENT_STORAGE_KEY)
        ) {
            sessionStorage.setItem(API_ENVIRONMENT_STORAGE_KEY, selectedApiEnvironment);
            dispatch(setApiEnvironment(selectedApiEnvironment));
        }
    };

    const handleReset = () => {
        setSelectedPreferences(new PreferencesState());
    };

    const warningDismiss = () => setShowWarning(false);

    const WarningModal = () => (
        <AppModal
            show={showWarning}
            title="Advarsel!"
            onClose={warningDismiss}
            rejectButton={
                <button
                    type="button"
                    className="c-preferences__button c-preferences__button--secondary"
                    onClick={warningDismiss}
                >
                    Nei
                </button>
            }
            acceptButton={
                <button
                    type="button"
                    className="c-preferences__button c-preferences__button--primary"
                    onClick={handleClose}
                >
                    Ja
                </button>
            }
        >
            <div className="c-preferences-toggle-content">
                Du har ulagrede endringer, er du sikker på at du vil fortsette?
            </div>
        </AppModal>
    );

    return (
        <>
            <WarningModal />
            <AppModal
                show={modalState === SplashState.PREFERENCES && !showWarning}
                onClose={handleClose}
                title="Innstillinger"
                className="c-preferences-modal"
                rejectButton={
                    <button
                        type="button"
                        className="c-preferences__button c-preferences__button--secondary"
                        onClick={handleReset}
                    >
                        Nullstill innstillinger
                    </button>
                }
                acceptButton={
                    <button
                        type="button"
                        className="c-preferences__button c-preferences__button--primary"
                        onClick={handleSubmit}
                    >
                        Lagre innstillinger
                    </button>
                }
            >
                <div className="c-preferences__toggle-content">
                    <h2 className="c-preferences__heading">Vegkart</h2>
                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={selectedPreferences['includeTrafikantgruppe'] as boolean}
                        label={'Vis trafikantgruppe i vegsystemreferanse'}
                        onChange={() => handleToggle('includeTrafikantgruppe')}
                    />

                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={
                            selectedPreferences['transformTinyGeometries'] as boolean
                        }
                        label={'Vis små strekningsgeometrier som punkt'}
                        onChange={() => handleToggle('transformTinyGeometries')}
                    />
                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={selectedPreferences['disableClusters'] as boolean}
                        label={'Vis punktgruppe som separate punkter'}
                        onChange={() => handleToggle('disableClusters')}
                    />
                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={selectedPreferences['showGeometryInfo'] as boolean}
                        label={'Vis geometriinformasjon for valgt objekt'}
                        onChange={() => handleToggle('showGeometryInfo')}
                    />
                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={selectedPreferences['fetchStatisticsLength'] as boolean}
                        label={'Vis veglengder i statistikk'}
                        onChange={() => handleToggle('fetchStatisticsLength')}
                    />
                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={selectedPreferences['showMetadata'] as boolean}
                        label={'Vis metadata for valgt objekt'}
                        onChange={() => handleToggle('showMetadata')}
                    />
                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={selectedPreferences['showPropertyBoundaries'] as boolean}
                        label={'Vis eiendomsgrenser'}
                        onChange={() => handleToggle('showPropertyBoundaries')}
                    />
                </div>
                <div className="c-preferences__toggle-content">
                    <h2 className="c-preferences__heading">Datakatalog</h2>
                    <SimpleSelect
                        label="Standard visningstype for datakatalog"
                        options={Object.values(DatakatalogMode)}
                        optionLabel={option => DATAKATALOG_MODE_LABELS[option]}
                        value={selectedPreferences.datakatalogDefaultMode}
                        onChange={handleDatakatalogModeSelect}
                    />
                </div>
                <div className="c-preferences__toggle-content">
                    <h2 className="c-preferences__heading">Avansert</h2>
                    <SimpleToggle
                        className="c-preferences__toggle"
                        checked={selectedPreferences['showDeveloperSettings'] as boolean}
                        label={'Vis innstillinger for utviklere'}
                        onChange={() => handleToggle('showDeveloperSettings')}
                    />
                </div>
                {selectedPreferences.showDeveloperSettings && (
                    <>
                        <div className="c-preferences__toggle-content">
                            <h3 className="c-preferences__heading">Generelt</h3>
                            <SimpleToggle
                                className="c-preferences__toggle"
                                checked={
                                    selectedPreferences[
                                        'includeTopoWithStandardMap'
                                    ] as boolean
                                }
                                label={
                                    'Standard kart bruker Kartverket når man er zoomet inn'
                                }
                                onChange={() =>
                                    handleToggle('includeTopoWithStandardMap')
                                }
                            />
                            <SimpleToggle
                                className="c-preferences__toggle"
                                checked={
                                    selectedPreferences[
                                        'useEgengeometriWhenAvailable'
                                    ] as boolean
                                }
                                label={'Bruk egengeometri når tilgjengelig'}
                                onChange={() =>
                                    handleToggle('useEgengeometriWhenAvailable')
                                }
                            />
                        </div>
                        <div className="c-preferences__toggle-content c-preferences__toggle-content--api-version">
                            <h3 className="c-preferences__heading">API-versjon</h3>
                            <SimpleSelect
                                label=""
                                options={[ApiVersion.V4, ApiVersion.V3]}
                                value={selectedApiVersion}
                                onChange={setSelectedApiVersion}
                            />
                        </div>
                        <div className="c-preferences__content c-preferences__content--api-version">
                            <p className="c-preferences__warning-text">
                                API-v4 er standardvalget i Vegkart. API-v3 avvikles snart,
                                men kan brukes hvis API-v4 skaper problemer. Valg av
                                API-versjon lagres på tvers av økter. Vi evaluerer API-v4
                                og kan rulle tilbake om nødvendig.
                            </p>
                            <p className="c-preferences__warning-text">
                                Følg med på nvdb.no for oppdatert informasjon:
                            </p>
                            <a
                                href="https://www.nvdb.no/aktuelt/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                https://www.nvdb.no/aktuelt/
                            </a>
                        </div>
                        <div className="c-preferences__toggle-content">
                            <h3 className="c-preferences__heading">API-miljø</h3>
                            <SimpleSelect
                                label=""
                                options={
                                    ['local', 'stm', 'dev'].includes(runtimeEnvironment())
                                        ? [
                                              'localhost',
                                              'localhosteksport',
                                              'local',
                                              'stm',
                                              'dev',
                                              'test',
                                              'prod',
                                              'elvegutv',
                                              'elvegtest',
                                              'elvegprod',
                                          ]
                                        : [
                                              'stm',
                                              'dev',
                                              'test',
                                              'prod',
                                              'elvegutv',
                                              'elvegtest',
                                              'elvegprod',
                                          ]
                                }
                                optionLabel={option => API_ENVIRONMENT_LABELS[option]}
                                value={selectedApiEnvironment}
                                onChange={handleEnvironmentSelect}
                            />
                            <p>
                                Velg hvilken konfigurasjonsfil Vegkart skal bruke, og
                                dermed hvilket miljø API-kallene går til.
                                {['local', 'stm', 'dev'].includes(
                                    runtimeEnvironment()
                                ) && (
                                    <>
                                        {' '}
                                        <i>localhost:8080</i>, tilsvarer utv, men med{' '}
                                        <i>restapi_url</i> satt til{' '}
                                        <i>http://localhost:8080</i>.{' '}
                                    </>
                                )}
                            </p>
                        </div>
                    </>
                )}
            </AppModal>
        </>
    );
};

PreferencesDialog.displayName = 'PreferencesDialog';
export default PreferencesDialog;
